import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { CONTACT_FORM } from "@/constants/testIds";

const EMAILS = [
  ["General Enquiries", "info@agrovesttvshow.com.ng"],
  ["Auditions & Registration", "auditions@agrovesttvshow.com.ng"],
  ["Sponsorship & Partnerships", "sponsors@agrovesttvshow.com.ng"],
  ["Media & Press", "media@agrovesttvshow.com.ng"],
  ["Youth Empowerment", "empowerment@agrovesttvshow.com.ng"],
  ["Government Relations", "govt@agrovesttvshow.com.ng"],
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 py-24">
      <div className="text-xs uppercase tracking-[0.3em] text-[#F26A1F] mb-5">
        Get in touch
      </div>
      <h1
        className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] mb-16"
        style={{ fontFamily: "'Cabinet Grotesk', 'Bricolage Grotesque', sans-serif" }}
      >
        Let's <span className="text-[#F26A1F]">talk</span>.
      </h1>

      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5 space-y-6">
          <div className="flex items-start gap-4">
            <MapPin className="text-[#F26A1F] mt-1" size={20} />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-1">Headquarters</div>
              <div className="text-white">Agrovest Media House<br />Abuja, FCT, Nigeria</div>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <Phone className="text-[#2BA84A] mt-1" size={20} />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-1">Phone</div>
              <a href="tel:+2347064056982" className="text-white hover:text-[#F26A1F]">+234 706 405 6982</a>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <Mail className="text-[#F26A1F] mt-1" size={20} />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Departments</div>
              <ul className="space-y-1.5">
                {EMAILS.map(([label, em]) => (
                  <li key={em} className="text-sm">
                    <span className="text-white/50">{label}: </span>
                    <a href={`mailto:${em}`} className="text-white hover:text-[#F26A1F]">{em}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid={CONTACT_FORM.form}
          className="md:col-span-7 p-8 border border-white/10 bg-black space-y-5"
        >
          <h2 className="text-2xl font-bold tracking-tight mb-2"
              style={{ fontFamily: "'Cabinet Grotesk', 'Bricolage Grotesque', sans-serif" }}>
            Send us a message
          </h2>
          <Field label="Name" value={form.name} onChange={update("name")} testid="contact-name-input" required />
          <Field label="Email" type="email" value={form.email} onChange={update("email")} testid="contact-email-input" required />
          <Field label="Subject" value={form.subject} onChange={update("subject")} testid="contact-subject-input" required />
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Message</label>
            <textarea
              value={form.message}
              onChange={update("message")}
              data-testid="contact-message-input"
              rows={5}
              required
              className="w-full p-3 bg-white/[0.03] border border-white/10 focus:border-[#F26A1F] outline-none text-sm rounded-sm"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid={CONTACT_FORM.submit}
            className="h-12 px-6 inline-flex items-center gap-2 rounded-sm bg-[#F26A1F] hover:bg-[#D95A15] disabled:opacity-60 text-white text-xs uppercase tracking-[0.18em] font-bold transition-colors"
          >
            {sending ? "Sending..." : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full h-12 px-3 bg-white/[0.03] border border-white/10 focus:border-[#F26A1F] outline-none text-sm rounded-sm"
      />
    </div>
  );
}
